import { api, unwrap } from '$lib/api/client';
import type { Household, HouseholdFeature, User } from '$lib/api/types';
import { departmentOf, departmentViews, type DepartmentView } from '$lib/domain/departments';
import { memberVisuals } from '$lib/domain/members';
import { placeOf, placeView, type PlaceView } from '$lib/domain/places';
import { i18n } from '$lib/i18n/i18n.svelte';
import { clearOutbox } from '$lib/offline/outbox';
import { forgetUser, identify } from '$lib/telemetry/telemetry';
import { tokens } from './tokens';

/** `setup`: the household's admin still has the setup flow ("Kom i gang") ahead. */
type Status = 'loading' | 'signedOut' | 'needsHousehold' | 'setup' | 'ready';

/** Who is signed in, and which household they belong to. */
class Session {
	status = $state<Status>('loading');
	user = $state<User | null>(null);
	/** Platform super-admins see the Admin page with every household. */
	isSuperAdmin = $state(false);
	household = $state<Household | null>(null);
	/**
	 * Set when this session created the household: its invite code is shown once the setup flow is done, with the
	 * address an invitation also went to.
	 */
	inviteCodePending = $state<{ invitedEmail: string | null } | null>(null);
	members = $derived(memberVisuals(this.household?.members ?? []));
	/** The household's storage places in display order, switched-off ones included so items anywhere get a name. */
	places: PlaceView[] = $derived((this.household?.places ?? []).map((p) => placeView(p, i18n.t)));
	/** The places that can hold items. */
	usablePlaces = $derived(this.places.filter((p) => p.enabled));
	/** The household's store departments in its shopping-list order; the built-in ones always come along. */
	departments: DepartmentView[] = $derived(
		departmentViews(this.household?.departments ?? [], i18n.t.departments)
	);
	/** Admins can change household-wide settings such as what the household shares. */
	isAdmin = $derived(
		this.household?.members.some((m) => m.userId === this.user?.id && m.isAdmin) ?? false
	);

	/** A place by key, also one the household no longer has. */
	placeOf(key: string): PlaceView {
		return placeOf(this.places, key, i18n.t);
	}

	/** A department by key; one the household doesn't know (deleted, another household's own) is "Andet". */
	departmentOf(key: string): DepartmentView {
		return departmentOf(this.departments, key);
	}

	/** Whether the platform super-admin switched the feature on for the household. */
	hasFeature(feature: HouseholdFeature): boolean {
		return this.household?.features.includes(feature) ?? false;
	}

	/** Restores the session from the refresh cookie. Works offline using the last cached profile. */
	async start() {
		const refreshed = await tokens.refresh();
		if (refreshed.kind === 'unauthorized') {
			this.status = 'signedOut';
			return;
		}
		await this.loadProfile();
	}

	async login(email: string, password: string) {
		const auth = await unwrap(api.POST('/api/auth/login', { body: { email, password } }));
		tokens.set(auth.accessToken);
		await this.loadProfile();
	}

	async register(name: string, email: string, password: string) {
		const auth = await unwrap(api.POST('/api/auth/register', { body: { name, email, password } }));
		tokens.set(auth.accessToken);
		await this.loadProfile();
	}

	/** Creates the household and goes on to its setup flow; the invite code is shown after it. */
	async createHousehold(name: string, inviteEmail: string | null): Promise<Household> {
		const household = await unwrap(api.POST('/api/households', { body: { name, inviteEmail } }));
		this.household = household;
		this.inviteCodePending = { invitedEmail: inviteEmail };
		this.status = this.statusOf(household);
		return household;
	}

	/** The setup flow was finished or skipped. */
	completeSetup(household: Household) {
		this.household = household;
		this.status = 'ready';
	}

	async joinHousehold(code: string) {
		this.household = await unwrap(api.POST('/api/households/join', { body: { code } }));
		this.status = 'ready';
	}

	/** The invite code was seen; signing up is over. */
	finishOnboarding() {
		this.inviteCodePending = null;
	}

	/** Replaces the household after a settings change. */
	setHousehold(household: Household) {
		this.household = household;
	}

	/** Reads the household again, so what another member changed (departments, stores) shows here too. */
	async refreshHousehold() {
		if (this.status !== 'ready') return;
		this.household = await unwrap(api.GET('/api/household'));
	}

	async logout() {
		// Before the token goes, so a flush that is running stops instead of refreshing it.
		await clearOutbox();
		await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
		tokens.clear();
		if ('caches' in window) await caches.delete('madplan-api');
		this.user = null;
		this.isSuperAdmin = false;
		this.household = null;
		this.inviteCodePending = null;
		this.status = 'signedOut';
		forgetUser();
	}

	/** Only an admin goes through the setup flow; other members start using the household as it is. */
	private statusOf(household: Household): Status {
		const isAdmin = household.members.some((m) => m.userId === this.user?.id && m.isAdmin);
		return !household.setupCompleted && isAdmin ? 'setup' : 'ready';
	}

	private async loadProfile() {
		try {
			const me = await unwrap(api.GET('/api/me'));
			this.user = me.user;
			this.isSuperAdmin = me.isSuperAdmin;
			this.household = me.household ?? null;
			this.status = me.household ? this.statusOf(me.household) : 'needsHousehold';
			identify(me.user.id, me.household?.id ?? null);
		} catch {
			this.status = 'signedOut';
		}
	}
}

export const session = new Session();
